// Remove the Supabase import since it's not used
// import { supabase } from '@/integrations/supabase/client';

/**
 * Interface for AI suggestion responses
 */
interface AISuggestionResponse {
  excerpt?: string;
  tags?: string[];
  metaDescription?: string;
  slug?: string;
}

// Use environment variable with relative path fallback
// Ensure the API URL is trimmed to avoid issues with trailing spaces
const API_URL = (import.meta.env.VITE_API_URL || '/api').trim();

/**
 * Helper function to clean quotation marks from responses
 */
const cleanQuotes = (text: string): string => {
  if (!text) return '';
  
  // Remove aspas no início e fim da string
  let cleaned = text.trim();
  
  // Remove aspas duplas no início e fim
  if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
    cleaned = cleaned.substring(1, cleaned.length - 1);
  }
  
  // Remove aspas simples no início e fim
  if (cleaned.startsWith("'") && cleaned.endsWith("'")) {
    cleaned = cleaned.substring(1, cleaned.length - 1);
  }
  
  // Remove aspas triplas (markdown) no início e fim
  if (cleaned.startsWith('```') && cleaned.endsWith('```')) {
    cleaned = cleaned.substring(3, cleaned.length - 3).trim();
  }
  
  // Remove frases introdutórias comuns
  cleaned = cleaned.replace(/^(aqui está|segue|segue abaixo|aqui vai|sugestão|sugestões)\s*:?\s*/i, '');
  
  // Remove aspas dentro do texto (para tags)
  cleaned = cleaned.replace(/["']/g, '');
  
  return cleaned.trim();
};

/**
 * Helper function to capitalize the first letter of each word in a string
 */
const capitalizeWords = (text: string): string => {
  if (!text) return '';
  
  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Makes an API call to generate suggestions based on content using the backend AI service
 */
export const generateContentSuggestions = async (
  content: string,
  title: string,
  type: 'excerpt' | 'tags' | 'metaDescription' | 'all' | 'slug'
): Promise<AISuggestionResponse> => {
  try {
    console.log(`Generating ${type} suggestion using backend AI service...`);
    
    // Prepare the content - limit length to avoid large payloads
    const contentSample = content.substring(0, 1500); // Limit content length for API call
    
    // Create the appropriate prompt based on the suggestion type
    let prompt = '';
    switch (type) {
      case 'excerpt':
        prompt = `Crie um resumo conciso para um post de blog sobre "${title}". O resumo deve ter entre 150 e 200 caracteres e ser atraente para leitores.`;
        break;
      case 'tags':
        prompt = `Sugira 3 tags SEO relevantes para um post de blog intitulado "${title}". 
        IMPORTANTE: Cada tag DEVE ter exatamente DUAS palavras (ex: "Perícia Judicial", "Engenharia Civil"). 
        As tags devem ser específicas, relevantes para busca no Google, e incluir termos técnicos relacionados à engenharia civil e perícia judicial.
        Formate a resposta como uma lista separada por vírgulas, sem numeração ou marcadores.`;
        break;
      case 'metaDescription':
        prompt = `Escreva uma meta descrição SEO otimizada para um post de blog intitulado "${title}". A descrição deve ter exatamente 150 a 160 caracteres, incluir palavras-chave relevantes, e convidar o leitor a clicar.`;
        break;
      case 'all':
        prompt = `Para um post de blog intitulado "${title}", forneça: 
        1. Um resumo conciso de 150-200 caracteres. 
        2. 6 tags SEO relevantes (separadas por vírgula), cada tag DEVE ter exatamente DUAS palavras (ex: "Perícia Judicial", "Engenharia Civil").
        3. Uma meta descrição de 150-160 caracteres otimizada para SEO.`;
        break;
      case 'slug':
        prompt = `Crie um slug SEO otimizado para um post de blog intitulado "${title}". O slug deve ser curto, descritivo e incluir palavras-chave relevantes.`;
        break;
    }
    
    // Call the backend AI service
    const response = await fetch(`${API_URL}/api/blog/suggest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        content: contentSample,
        type,
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Backend AI service error:', errorText);
      throw new Error(`Erro no serviço de IA: ${response.status} ${errorText}`);
    }
    
    const data = await response.json();
    console.log('AI response received:', data);
    
    // Process the response based on the type
    if (type === 'all') {
      return {
        excerpt: data.excerpt ? cleanQuotes(data.excerpt) : undefined,
        tags: data.tags ? data.tags.map(cleanQuotes) : undefined,
        metaDescription: data.metaDescription ? cleanQuotes(data.metaDescription) : undefined,
        slug: data.slug ? cleanQuotes(data.slug) : undefined,
      };
    } else if (type === 'tags') {
      // For tags, ensure we have an array
      let tags: string[] = [];
      
      // Check if we have a suggestion field in the response
      if (data.suggestion) {
        const suggestionText = cleanQuotes(data.suggestion);
        
        // Split by comma and clean each tag
        tags = suggestionText
          .split(',')
          .map(tag => cleanQuotes(tag.trim()))
          .filter(tag => tag.length > 0);
      } else if (Array.isArray(data.tags)) {
        tags = data.tags.map(cleanQuotes);
      }
      
      // Ensure we have valid tags
      if (tags.length === 0) {
        console.warn('No valid tags found in response:', data.suggestion);
        return { tags: [] };
      }
      
      return { tags };
    } else {
      // For excerpt, metaDescription, and slug, return as string
      const cleanedResult = data.suggestion ? cleanQuotes(data.suggestion) : '';
      return type === 'excerpt' 
        ? { excerpt: cleanedResult } 
        : type === 'metaDescription' 
          ? { metaDescription: cleanedResult } 
          : { slug: cleanedResult };
    }
  } catch (error) {
    console.error('Error generating AI suggestions:', error);
    throw error; // Re-throw to handle in the component
  }
};

/**
 * Helper to get suggestions for excerpt
 */
export const suggestExcerpt = async (content: string, title: string): Promise<string> => {
  const result = await generateContentSuggestions(content, title, 'excerpt');
  return result.excerpt || '';
};

/**
 * Suggest tags for a blog post
 */
export const suggestTags = async (content: string, title: string): Promise<string[]> => {
  try {
    console.log('Generating tag suggestions...');
    const suggestions = await generateContentSuggestions(content, title, 'tags');
    
    if (!suggestions.tags || suggestions.tags.length === 0) {
      console.warn('No tag suggestions were generated');
      return [];
    }
    
    // Process tags: clean, capitalize first letter of each word, and filter for two-word tags
    const processedTags = suggestions.tags
      .map(tag => cleanQuotes(tag))
      .filter(tag => tag.trim() !== '')
      // Prioritize two-word tags
      .filter(tag => {
        const words = tag.split(' ').filter(word => word.trim() !== '');
        return words.length === 2; // Only keep tags with exactly two words
      })
      .map(tag => capitalizeWords(tag))
      .slice(0, 3); // Limit to 3 tags
    
    // If we don't have enough two-word tags, add some single-word tags as fallback
    if (processedTags.length < 3) {
      const singleWordTags = suggestions.tags
        .map(tag => cleanQuotes(tag))
        .filter(tag => tag.trim() !== '')
        .filter(tag => tag.split(' ').filter(word => word.trim() !== '').length === 1)
        .map(tag => capitalizeWords(tag))
        .slice(0, 3 - processedTags.length);
      
      processedTags.push(...singleWordTags);
    }
    
    console.log('Generated tags:', processedTags);
    return processedTags;
  } catch (error) {
    console.error('Error suggesting tags:', error);
    throw error;
  }
};

/**
 * Helper to get suggestions for meta description
 */
export const suggestMetaDescription = async (content: string, title: string): Promise<string> => {
  try {
    const result = await generateContentSuggestions(content, title, 'metaDescription');
    let metaDescription = result.metaDescription || '';
    
    // Enforce 150 character limit
    if (metaDescription.length > 150) {
      metaDescription = metaDescription.substring(0, 150);
    }
    
    return metaDescription;
  } catch (error) {
    console.error('Error suggesting meta description:', error);
    return '';
  }
};

/**
 * Suggest an SEO-optimized slug for a blog post
 */
export const suggestSlug = async (title: string, content: string): Promise<string> => {
  try {
    console.log('Generating SEO-optimized slug suggestion...');
    
    // If we have content, use it to provide context for a better slug
    const contextText = content ? content.substring(0, 1500) : '';
    
    const response = await generateContentSuggestions(contextText, title, 'slug');
    
    if (!response.slug) {
      console.warn('No slug suggestion was generated');
      return '';
    }
    
    // Clean and format the slug
    const slug = cleanQuotes(response.slug);
    console.log('Generated SEO-optimized slug:', slug);
    
    return slug;
  } catch (error) {
    console.error('Error suggesting slug:', error);
    return '';
  }
};
